import React, { useEffect, useRef } from 'react';
import MessageBubble from './components/MessageBubble';
import Button from '../../components/Button';
import TaskPrompt from './components/TaskPrompt';

const prettyState = (s) => (s || '').split('_').join(' ').toLowerCase();

const fmt = (secs) => {
  if (secs == null) return '';
  const s = Math.max(0, Math.floor(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Live chat screen — transcript + input + overall timer/progress + End button. */
const AIInterviewChat = ({
  messages,
  input,
  onInputChange,
  onSend,
  onEnd,
  loading,
  currentState,
  stage,
  silenceLeft,
  nudgeAfter,
  endAfter,
  elapsed,
  total,
  questionsAsked,
  targetQuestions,
  closingSeconds,
  activeTask,
  taskAnswer,
  onTaskAnswerChange,
  onTaskSubmit,
}) => {
  const scrollRef = useRef(null);
  const closing = closingSeconds != null;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, closing]);

  const remaining = total != null && elapsed != null ? total - elapsed : null;
  const taskActive = activeTask && activeTask.status === 'active';
  const overallColor = remaining != null && remaining <= 60
    ? 'text-red-600 dark:text-red-300'
    : remaining != null && remaining <= 180
      ? 'text-amber-600 dark:text-amber-300'
      : 'text-slate-500 dark:text-slate-300';

  const pastNudge = silenceLeft != null && endAfter != null && nudgeAfter != null
    && silenceLeft <= endAfter - nudgeAfter;
  const silenceColor = silenceLeft != null && silenceLeft <= 30
    ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'
    : pastNudge
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300';

  return (
    <div
      className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col"
      style={{ height: '72vh' }}
    >
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-gray-100 dark:border-gray-800 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-700 dark:text-slate-200">Interview in progress</span>
          {targetQuestions != null && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 font-medium">
              Q {Math.max(1, questionsAsked || 1)} / ~{targetQuestions}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {total != null && (
            <span className={`text-xs font-medium tabular-nums ${overallColor}`} title="Overall interview time">
              ⏱ {fmt(elapsed)} / {fmt(total)}
            </span>
          )}
          {silenceLeft != null && !closing && !taskActive && (
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${silenceColor}`}
              title="The interviewer checks in, then pauses, if there's no response.">
              ⏳ {fmt(silenceLeft)}
            </span>
          )}
          {taskActive && (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 tabular-nums">
              Task {fmt(activeTask.remaining_seconds)}
            </span>
          )}
          {(stage || currentState) && (
            <span className="text-xs px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-300 capitalize">
              {stage || prettyState(currentState)}
            </span>
          )}
          <button
            type="button"
            onClick={onEnd}
            disabled={loading || closing}
            className="text-xs px-3 py-1 rounded-full border border-red-200 text-red-500 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-900/20 disabled:opacity-50 transition"
          >
            End interview
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
        {messages.map((m, i) => (
          <MessageBubble key={i} role={m.role} text={m.text} />
        ))}
        {loading && !closing && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-800 text-gray-400 rounded-2xl px-4 py-2.5 text-sm italic">
              the interviewer is typing…
            </div>
          </div>
        )}
      </div>

      {closing ? (
        <div className="border-t border-gray-100 dark:border-gray-800 p-4 text-center text-sm text-slate-500 dark:text-slate-300">
          Interview complete — preparing your summary in {closingSeconds}s…
        </div>
      ) : taskActive ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onTaskSubmit();
          }}
          className="border-t border-gray-100 dark:border-gray-800 p-4 space-y-3"
        >
          <div className="rounded-lg border border-indigo-100 bg-indigo-50/70 p-4 dark:border-indigo-900/50 dark:bg-indigo-950/30">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">Practical task</div>
                <h3 className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{activeTask.title || 'Practical task'}</h3>
              </div>
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-indigo-700 shadow-sm dark:bg-gray-900 dark:text-indigo-300 tabular-nums">
                {fmt(activeTask.remaining_seconds)}
              </span>
            </div>
            <TaskPrompt
              text={activeTask.candidate_prompt}
              className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-200"
            />
          </div>
          <textarea
            value={taskAnswer}
            rows={6}
            onChange={(e) => onTaskAnswerChange(e.target.value)}
            disabled={loading}
            placeholder="Write your task answer here..."
            className="w-full resize-none rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm leading-6 text-gray-900 placeholder-gray-400 transition focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
          <div className="flex justify-end">
            <Button type="submit" variant="primary" size="md" disabled={loading || !taskAnswer.trim()}>
              Submit task answer
            </Button>
          </div>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSend();
          }}
          className="border-t border-gray-100 dark:border-gray-800 p-3 flex gap-2"
        >
          <textarea
            value={input}
            rows={1}
            onChange={(e) => {
              onInputChange(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
            }}
            // Enter sends; Shift+Enter inserts a newline (so multi-line answers / code work).
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!loading && input.trim()) onSend(); } }}
            disabled={loading}
            placeholder="Type your response…  (Shift+Enter for a new line)"
            className="flex-1 resize-none px-4 py-2.5 leading-relaxed rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
          />
          <Button type="submit" variant="primary" size="md" disabled={loading || !input.trim()}>
            Send
          </Button>
        </form>
      )}
    </div>
  );
};

export default AIInterviewChat;
